import { encodeEntityId } from "@/hooks/useEntityIdParam";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import { NavigationListenerDataType } from "@WahooFitness/wahoo-offline-mfe";
import { KEntityType } from "@WahooFitness/wsm-native/dist/esm/types/kc/KEntityType";

function getExplorePageFromWorkoutFamily(family: WahooWorkoutTypeFamily): string | undefined {
  switch (+family) {
    case WahooWorkoutTypeFamily.Cycling:
      return "cycling";
    case WahooWorkoutTypeFamily.Running:
      return "running";
    case WahooWorkoutTypeFamily.Yoga:
      return "yoga";
    case WahooWorkoutTypeFamily.Strength:
      return "strength";
    case WahooWorkoutTypeFamily.Mental:
    case WahooWorkoutTypeFamily.MentalFitness:
      return "mental";
    default:
      return undefined;
  }
}

/**
 * This function is used to transform links from the lobby mfe to the appropriate routes in the app.
 * @param path The path of the wahoo link e.g. /plans
 * @param params The query params of the wahoo link
 * @returns A NavigationListenerDataType object or undefined if no matching route is found
 */
export function wahooLinkTransform(
  path: string,
  params: URLSearchParams
): NavigationListenerDataType | undefined {
  const [_empty, resource, resourceId] = path.split("/");

  switch (resource) {
    case "plans":
      if (params.get("workout_type_family_id")) {
        try {
          const sport = getExplorePageFromWorkoutFamily(
            params.get("workout_type_family_id") as unknown as WahooWorkoutTypeFamily
          );
          if (!sport) {
            return { url: "/plan" };
          }
          return {
            url: `/plan/${sport}`,
          };
        } catch {
          return { url: "/plan" };
        }
      }
      // as of now systm sends either:
      // https://link.wahoofitness.com/apps/wahoo_app/plans?plan_id=undefined&workout_id=12345
      // OR https://link.wahoofitness.com/apps/wahoo_app/plans?plan_id=12345&workout_id=undefined
      if (params.get("plan_id") && params.get("plan_id") !== "undefined") {
        return {
          url: `/plan-details?planId=${params.get("plan_id")}`,
        };
      }
      if (params.get("workout_id") && params.get("workout_id") !== "undefined") {
        return {
          url: `/plan-details?workoutId=${params.get("workout_id")}`,
        };
      }
      if (resourceId) {
        return {
          url: `/plan-details?planId=${resourceId}`,
        };
      }
      return { url: "/plan" };
    case "routes":
      // all routes deep-links will be to curated routes, no need to check provider type
      if (params.get("workout_type_family_id")) {
        return {
          url: `/routes/curated?${params.toString()}`,
        };
      }
      if (resourceId) {
        return {
          url: `/routes/find?cloudId=${resourceId}&action=details`,
        };
      }
      return { url: "/routes/curated" };
    case "workout_summaries":
      if (resourceId) {
        const entityId = encodeEntityId({
          id: Number(resourceId),
          kEntityType: KEntityType.WORKOUT_SUMMARY,
        });
        return { url: `/history/activity-details/entity/${entityId}` };
      }
      return { url: "/history" };
    case "plan_recommendation_settings":
      return { url: "/?recommendation-settings-open=true" };
    case "mental_fitness": {
      if (params.get("page") === "details") {
        if (params.get("category") && params.get("recoveryId")) {
          return {
            url: `/mental-fitness-details?category=${params.get("category")}&recoveryId=${params.get("recoveryId")}`,
          };
        }
      }
      return undefined;
    }
    case "training_progressions":
      return { url: "/training-progress/fitness-score" };
    case "training_readiness":
      return { url: "/training-readiness" };
    default:
      return undefined;
  }
}
